import { CheckedAddress, promiseAll } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions } from '@tanstack/react-query'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'
import { z } from 'zod'

import { airdropBreakdownResponseSchema } from './airdropBreakdownResponseSchema'
import { cookie3AirdropRewardsAddress, ignitionAirdropRewardsAddress } from '@/config/abis/addresses'
import { SPK_AIRDROP_DEMASK_KEY, spark2ApiUrl } from '@/config/consts'
import { sparkRewardsAbi } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'

export interface AidropBreakdownQueryOptionsParams {
  address: CheckedAddress | undefined
  wagmiConfig: Config
  chainId: number
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function airdropBreakdownQueryOptions({ address, wagmiConfig, chainId }: AidropBreakdownQueryOptionsParams) {
  return queryOptions({
    queryKey: airdropBreakdownQueryKey({ address }),
    queryFn: () => airdropBreakdownQueryFunction({ address, wagmiConfig, chainId }),
  })
}

export function airdropBreakdownQueryKey({ address }: { address: CheckedAddress | undefined }): QueryKey {
  return ['spk-airdrop-breakdown', address]
}

export type AirdropBreakdownQueryData = {
  allocations: z.infer<typeof airdropBreakdownResponseSchema>
  ignitionActive: boolean
  overdriveActive: boolean
} | null

export async function airdropBreakdownQueryFunction({
  address,
  wagmiConfig,
  chainId,
}: AidropBreakdownQueryOptionsParams): Promise<AirdropBreakdownQueryData> {
  if (!address) {
    return null
  }
  const { ignitionRoot, cookie3Root } = await promiseAll(
    {
      ignitionRoot: () =>
        readContract(wagmiConfig, {
          address: getContractAddress(ignitionAirdropRewardsAddress, chainId),
          abi: sparkRewardsAbi,
          functionName: 'merkleRoot',
          chainId,
        }),
      cookie3Root: () =>
        readContract(wagmiConfig, {
          address: getContractAddress(cookie3AirdropRewardsAddress, chainId),
          abi: sparkRewardsAbi,
          functionName: 'merkleRoot',
          chainId,
        }),
    },
    { concurrency: 5 },
  )

  const queryParams = new URLSearchParams()
  queryParams.set('ignition_root', ignitionRoot)
  queryParams.set('cookie3_root', cookie3Root)
  if (typeof SPK_AIRDROP_DEMASK_KEY === 'string' && SPK_AIRDROP_DEMASK_KEY.length > 0) {
    queryParams.set('demask_key', SPK_AIRDROP_DEMASK_KEY)
  }

  const url = `${spark2ApiUrl}/spk-airdrop/${address}/?${queryParams.toString()}`

  const res = await fetch(url)
  if (!res.ok) {
    throw new Error('Failed to fetch airdrop breakdown')
  }

  const data = airdropBreakdownResponseSchema.parse(await res.json())
  return {
    allocations: data,
    ignitionActive: data.some(
      (item) => item.type === 'spark-ignition' && item.root_hash.toLowerCase() === ignitionRoot.toLowerCase(),
    ),
    overdriveActive: data.some(
      (item) => item.type === 'overdrive' && item.root_hash.toLowerCase() === ignitionRoot.toLowerCase(),
    ),
  }
}
